import * as React from "react";
import { twMerge } from "tailwind-merge";
import Text from "./Text";

export type ChipType =
  | "success"
  | "warning"
  | "critical"
  | "neutral"
  | "info"
  | "caution"
  | "verified";

export type ChipSize = "sm" | "md" | "lg";

export type ChipProps = {
  label: string;
  type?: ChipType;
  size?: ChipSize;
  solid?: boolean;
  icon?: React.ReactNode;
  className?: string;
};

const typeClass: Record<ChipType, { outline: string; solid: string }> = {
  success: {
    outline: "border-emerald-700 bg-emerald-50 text-emerald-700",
    solid: "border-emerald-700 bg-emerald-700 text-white",
  },
  warning: {
    outline: "border-amber-300 bg-amber-50 text-amber-900",
    solid: "border-amber-600 bg-amber-600 text-white",
  },
  critical: {
    outline: "border-red-300 bg-red-50 text-red-800",
    solid: "border-red-600 bg-red-600 text-white",
  },
  neutral: {
    outline: "border-emerald-700 bg-emerald-100 text-emerald-950",
    solid: "border-emerald-700 bg-emerald-700 text-white",
  },
  info: {
    outline: "border-sky-300 bg-sky-50 text-sky-800",
    solid: "border-sky-600 bg-sky-600 text-white",
  },
  caution: {
    outline: "border-orange-300 bg-orange-50 text-orange-900",
    solid: "border-orange-600 bg-orange-600 text-white",
  },
  verified: {
    outline: "border-cyan-600 bg-cyan-50 text-cyan-800",
    solid: "border-cyan-600 bg-cyan-600 text-white",
  },
};

const sizeClass: Record<ChipSize, string> = {
  sm: "h-[22px] gap-[4px] px-[8px] text-[11px]",
  md: "h-[26px] gap-[6px] px-[10px] text-[12px]",
  lg: "h-[32px] gap-[8px] px-[12px] text-[13px]",
};

export default function Chip({
  label,
  type = "neutral",
  size = "sm",
  solid = false,
  icon,
  className,
}: ChipProps) {
  return (
    <span
      className={twMerge(
        "inline-flex max-w-full items-center rounded-[4px] border font-[600] tracking-[0.02em]",
        typeClass[type][solid ? "solid" : "outline"],
        sizeClass[size],
        className,
      )}
    >
      {icon ? (
        <span className="inline-flex shrink-0 items-center">{icon}</span>
      ) : null}
      <Text as="span" level={5} className="truncate font-[600]">
        {label}
      </Text>
    </span>
  );
}
